export const HALIFAX_TZ = "America/Halifax";

/** Shown when a ticket / log timestamp is missing or unparseable. Never throw. */
export const CLOCK_PLACEHOLDER = "—";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

type DateParts = {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
};

const INVALID_PARTS: DateParts = {
  year: "0000",
  month: "01",
  day: "01",
  hour: "00",
  minute: "00",
  second: "00",
};

/** Ticket STD/STA, log stamps, sync payloads — anything a board cell might wrap in `new Date(...)`. */
export type ClockInstant = Date | string | number | null | undefined;

export function isValidDate(date: unknown): date is Date {
  return date instanceof Date && Number.isFinite(date.getTime());
}

export function coerceClockDate(value: ClockInstant): Date | null {
  if (value instanceof Date) return isValidDate(value) ? value : null;
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    const date = new Date(value);
    return isValidDate(date) ? date : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const date = new Date(trimmed);
    return isValidDate(date) ? date : null;
  }
  return null;
}

function formatOrPlaceholder(value: ClockInstant, write: (date: Date) => string): string {
  const date = coerceClockDate(value);
  if (!date) return CLOCK_PLACEHOLDER;
  try {
    return write(date);
  } catch {
    return CLOCK_PLACEHOLDER;
  }
}

/** Keep a parseable ISO; empty / garbage becomes "" so renderers show — (never invent a time). */
export function sanitizeClockIso(value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  return coerceClockDate(trimmed) ? trimmed : "";
}

export function safeIsoString(value: ClockInstant): string {
  const date = coerceClockDate(value);
  if (!date) return "";
  try {
    return date.toISOString();
  } catch {
    return "";
  }
}

function formatPartsInZone(date: Date, timeZone: string): DateParts {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const map: Record<string, string> = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== "literal") map[part.type] = part.value;
  }
  return {
    year: map.year ?? "0000",
    month: map.month ?? "01",
    day: map.day ?? "01",
    hour: map.hour === "24" ? "00" : (map.hour ?? "00"),
    minute: map.minute ?? "00",
    second: map.second ?? "00",
  };
}

export function partsInTimeZone(date: Date, timeZone: string): DateParts {
  if (!isValidDate(date)) return INVALID_PARTS;
  try {
    return formatPartsInZone(date, timeZone);
  } catch {
    if (timeZone !== "UTC") {
      try {
        return formatPartsInZone(date, "UTC");
      } catch {
        return INVALID_PARTS;
      }
    }
    return INVALID_PARTS;
  }
}

function formatClockLine(p: DateParts, weekday: string, zone: string): string {
  const mon = MONTHS[Number(p.month) - 1] ?? p.month;
  return `${weekday} ${p.day} ${mon} ${p.year}  ${p.hour}:${p.minute}:${p.second} ${zone}`;
}

export function halifaxParts(date: Date = new Date()): DateParts {
  return partsInTimeZone(date, HALIFAX_TZ);
}

export function utcParts(date: Date = new Date()): DateParts {
  return partsInTimeZone(date, "UTC");
}

export function minutesOfDayInTimeZone(date: Date, timeZone: string): number {
  const p = partsInTimeZone(date, timeZone);
  return Number(p.hour) * 60 + Number(p.minute);
}

export function wallTimeInTimeZone(
  timeZone: string,
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second = 0,
): Date {
  const utc = Date.UTC(year, month - 1, day, hour, minute, second);
  const first = new Date(utc);
  const once = new Date(utc - tzOffsetMsInZone(first, timeZone));
  return new Date(utc - tzOffsetMsInZone(once, timeZone));
}

function tzOffsetMsInZone(date: Date, timeZone: string): number {
  const p = partsInTimeZone(date, timeZone);
  const asUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
  );
  return asUtc - date.getTime();
}

function tzOffsetMs(date: Date): number {
  const p = halifaxParts(date);
  const asUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
  );
  return asUtc - date.getTime();
}

export function halifaxAbbr(date: Date): string {
  if (!isValidDate(date)) return "UTC";
  try {
    const hours = Math.round(tzOffsetMs(date) / 3_600_000);
    if (!Number.isFinite(hours)) return "UTC";
    if (hours === -3) return "ADT";
    if (hours === -4) return "AST";
    return `UTC${hours >= 0 ? "+" : ""}${hours}`;
  } catch {
    return "UTC";
  }
}

export function halifaxWallToDate(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second = 0,
): Date {
  const utc = Date.UTC(year, month - 1, day, hour, minute, second);
  let date = new Date(utc);
  date = new Date(utc - tzOffsetMs(date));
  return new Date(utc - tzOffsetMs(date));
}

export function formatUtcClock(date?: ClockInstant): string {
  return formatOrPlaceholder(arguments.length === 0 ? new Date() : date, (d) => {
    const p = utcParts(d);
    const wd = WEEKDAYS[d.getUTCDay()] ?? "";
    return formatClockLine(p, wd, "UTC");
  });
}

export function formatHalifaxCardTime(date?: ClockInstant): string {
  return formatOrPlaceholder(arguments.length === 0 ? new Date() : date, (d) => {
    const p = halifaxParts(d);
    return `${p.hour}:${p.minute}:${p.second}`;
  });
}

export function formatHalifaxCardDate(date?: ClockInstant): string {
  return formatOrPlaceholder(arguments.length === 0 ? new Date() : date, (d) => {
    const p = halifaxParts(d);
    const mon = MONTHS[Number(p.month) - 1] ?? p.month;
    return `${p.day} ${mon} ${p.year}`;
  });
}

export function formatUtcCardTime(date?: ClockInstant): string {
  return formatOrPlaceholder(arguments.length === 0 ? new Date() : date, (d) => {
    const p = utcParts(d);
    return `${p.hour}:${p.minute}:${p.second}`;
  });
}

export function formatUtcCardDate(date?: ClockInstant): string {
  return formatOrPlaceholder(arguments.length === 0 ? new Date() : date, (d) => {
    const p = utcParts(d);
    const mon = MONTHS[Number(p.month) - 1] ?? p.month;
    return `${p.day} ${mon} ${p.year}`;
  });
}

/** Board column time — Halifax hours:minutes + zone. */
export function formatHalifaxHm(date: ClockInstant): string {
  return formatOrPlaceholder(date, (d) => {
    const p = halifaxParts(d);
    return `${p.hour}:${p.minute} ${halifaxAbbr(d)}`;
  });
}

/** Board / schedule column time — Zulu hours:minutes. */
export function formatUtcHm(date: ClockInstant): string {
  return formatOrPlaceholder(date, (d) => {
    const p = utcParts(d);
    return `${p.hour}:${p.minute}Z`;
  });
}

/**
 * Civil abbreviations for zones whose ICU short name is often `GMT±H`
 * (Newfoundland) or a generic offset. Standard, then daylight.
 * Equal pairs do not observe daylight saving.
 */
const ZONE_ABBR: Record<string, readonly [string, string]> = {
  "America/Halifax": ["AST", "ADT"],
  "America/Glace_Bay": ["AST", "ADT"],
  "America/Moncton": ["AST", "ADT"],
  "America/Goose_Bay": ["AST", "ADT"],
  "America/Blanc-Sablon": ["AST", "AST"],
  "America/Puerto_Rico": ["AST", "AST"],
  "America/St_Johns": ["NST", "NDT"],
  "America/New_York": ["EST", "EDT"],
  "America/Toronto": ["EST", "EDT"],
  "America/Detroit": ["EST", "EDT"],
  "America/Indiana/Indianapolis": ["EST", "EDT"],
  "America/Indiana/Marengo": ["EST", "EDT"],
  "America/Indiana/Petersburg": ["EST", "EDT"],
  "America/Indiana/Vincennes": ["EST", "EDT"],
  "America/Kentucky/Louisville": ["EST", "EDT"],
  "America/Kentucky/Monticello": ["EST", "EDT"],
  "America/Nassau": ["EST", "EDT"],
  "America/Iqaluit": ["EST", "EDT"],
  "America/Thunder_Bay": ["EST", "EDT"],
  "America/Atikokan": ["EST", "EST"],
  "America/Jamaica": ["EST", "EST"],
  "America/Cancun": ["EST", "EST"],
  "America/Panama": ["EST", "EST"],
  "America/Cayman": ["EST", "EST"],
  "America/Chicago": ["CST", "CDT"],
  "America/Winnipeg": ["CST", "CDT"],
  "America/Indiana/Knox": ["CST", "CDT"],
  "America/Indiana/Tell_City": ["CST", "CDT"],
  "America/Menominee": ["CST", "CDT"],
  "America/Matamoros": ["CST", "CDT"],
  "America/Regina": ["CST", "CST"],
  "America/Swift_Current": ["CST", "CST"],
  "America/Mexico_City": ["CST", "CST"],
  "America/Monterrey": ["CST", "CST"],
  "America/Merida": ["CST", "CST"],
  "America/Bahia_Banderas": ["CST", "CST"],
  "America/Chihuahua": ["CST", "CST"],
  "America/Denver": ["MST", "MDT"],
  "America/Edmonton": ["MST", "MDT"],
  "America/Boise": ["MST", "MDT"],
  "America/Cambridge_Bay": ["MST", "MDT"],
  "America/Yellowknife": ["MST", "MDT"],
  "America/Phoenix": ["MST", "MST"],
  "America/Creston": ["MST", "MST"],
  "America/Dawson_Creek": ["MST", "MST"],
  "America/Fort_Nelson": ["MST", "MST"],
  "America/Hermosillo": ["MST", "MST"],
  "America/Mazatlan": ["MST", "MST"],
  "America/Los_Angeles": ["PST", "PDT"],
  "America/Vancouver": ["PST", "PDT"],
  "America/Tijuana": ["PST", "PDT"],
  "America/Anchorage": ["AKST", "AKDT"],
  "America/Juneau": ["AKST", "AKDT"],
  "America/Sitka": ["AKST", "AKDT"],
  "America/Nome": ["AKST", "AKDT"],
  "America/Yakutat": ["AKST", "AKDT"],
  "Pacific/Honolulu": ["HST", "HST"],
  "America/Adak": ["HST", "HDT"],
  "Atlantic/Bermuda": ["AST", "ADT"],
  "America/Barbados": ["AST", "AST"],
  "America/Lower_Princes": ["AST", "AST"],
  "America/Santo_Domingo": ["AST", "AST"],
  "America/St_Thomas": ["AST", "AST"],
  "America/Port_of_Spain": ["AST", "AST"],
  "America/Aruba": ["AST", "AST"],
  "America/Martinique": ["AST", "AST"],
  "America/Curacao": ["AST", "AST"],
  "Europe/London": ["GMT", "BST"],
  "Europe/Dublin": ["GMT", "IST"],
  "Europe/Lisbon": ["WET", "WEST"],
  "Europe/Paris": ["CET", "CEST"],
  "Atlantic/Reykjavik": ["GMT", "GMT"],
};

function isDaylightSaving(date: Date, timeZone: string): boolean {
  const year = date.getUTCFullYear();
  const jan = tzOffsetMsInZone(new Date(Date.UTC(year, 0, 15, 12, 0, 0)), timeZone);
  const jul = tzOffsetMsInZone(new Date(Date.UTC(year, 6, 15, 12, 0, 0)), timeZone);
  if (jan === jul) return false;
  return tzOffsetMsInZone(date, timeZone) > Math.min(jan, jul);
}

function intlZoneName(date: Date, timeZone: string, style: "short" | "long"): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: style })
      .formatToParts(date)
      .find((row) => row.type === "timeZoneName");
    return part?.value?.trim() ?? "";
  } catch {
    return "";
  }
}

/** First letters of a civil long name: "Eastern Daylight Time" → EDT. */
function letterAbbrFromZoneName(name: string): string | null {
  const lower = name.toLowerCase();
  if (!lower || /^gmt|^utc/.test(lower)) return null;
  if (lower.includes("alaska") && lower.includes("daylight")) return "AKDT";
  if (lower.includes("alaska") && lower.includes("standard")) return "AKST";
  if (lower.includes("hawaii")) return "HST";
  if (lower.includes("greenwich")) return "GMT";
  if (lower.includes("mexican pacific")) return "MST";
  const words = name.replace(/[^A-Za-z]+/g, " ").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return null;
  const content = words.filter((word) => word.toLowerCase() !== "time");
  const skipStandard = content.length >= 3;
  const letters: string[] = [];
  for (const word of words) {
    const token = word.toLowerCase();
    if (token === "time" || token === "mean") continue;
    if (token === "standard" && skipStandard) continue;
    letters.push(word[0]!.toUpperCase());
  }
  const abbr = letters.join("");
  if (!/^[A-Z]{2,5}$/.test(abbr) || abbr === "UTC" || abbr === "GMT") return null;
  return abbr;
}

function offsetAbbr(date: Date, timeZone: string): string {
  const hours = tzOffsetMsInZone(date, timeZone) / 3_600_000;
  if (!Number.isFinite(hours)) return timeZone;
  if (Math.abs(hours) < 1e-6) return "Z";
  const rounded = Math.round(hours * 2) / 2;
  const text = Number.isInteger(rounded) ? String(rounded) : String(rounded);
  return `UTC${rounded > 0 ? "+" : ""}${text}`;
}

/** Short zone label. UTC is always `Z`. Never the PC zone, never a hard-coded ADT. */
export function zoneAbbr(date: Date, timeZone: string): string {
  if (!isValidDate(date)) return timeZone === "UTC" ? "Z" : "UTC";
  const zone = timeZone.trim() || "UTC";
  if (zone === "UTC" || zone === "Etc/UTC" || zone === "Etc/GMT") return "Z";
  const pair = ZONE_ABBR[zone];
  if (pair) {
    if (pair[0] === pair[1]) return pair[0];
    return isDaylightSaving(date, zone) ? pair[1] : pair[0];
  }
  if (zone === HALIFAX_TZ) return halifaxAbbr(date);
  const fromLong = letterAbbrFromZoneName(intlZoneName(date, zone, "long"));
  if (fromLong) return fromLong;
  const short = intlZoneName(date, zone, "short");
  if (/^[A-Za-z]{2,5}$/.test(short) && !/^(UTC|GMT)$/i.test(short)) return short.toUpperCase();
  return offsetAbbr(date, zone);
}

/** Hours:minutes in a zone. UTC / zuluSuffix paints a trailing `Z`. */
export function formatHmInZone(date: ClockInstant, timeZone: string, zulu = timeZone === "UTC"): string {
  return formatOrPlaceholder(date, (d) => {
    const p = partsInTimeZone(d, timeZone);
    return zulu ? `${p.hour}:${p.minute}Z` : `${p.hour}:${p.minute} ${zoneAbbr(d, timeZone)}`;
  });
}

export function formatCardDateInZone(date: ClockInstant, timeZone: string): string {
  return formatOrPlaceholder(date, (d) => {
    const p = partsInTimeZone(d, timeZone);
    const mon = MONTHS[Number(p.month) - 1] ?? p.month;
    return `${p.day} ${mon} ${p.year}`;
  });
}

function weekdayNameInZone(date: Date, timeZone: string): string {
  const p = partsInTimeZone(date, timeZone);
  const idx = new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), 12, 0, 0)).getUTCDay();
  return WEEKDAYS[idx] ?? "";
}

/** Full desk line in an IANA zone. `zoneLabel` overrides the abbreviation (ADT*, Z). */
export function formatClockInZone(date: ClockInstant, timeZone: string, zoneLabel?: string): string {
  const zone = timeZone.trim() || "UTC";
  return formatOrPlaceholder(date, (d) => {
    const p = partsInTimeZone(d, zone);
    const label = zoneLabel?.trim() || (zone === "UTC" ? "Z" : zoneAbbr(d, zone));
    return formatClockLine(p, weekdayNameInZone(d, zone), label);
  });
}

/** Rail / status clock. UTC paints `Z` unless a label is passed. */
export function formatZoneShort(date: ClockInstant, timeZone: string, zoneLabel?: string): string {
  const zone = timeZone.trim() || "UTC";
  return formatOrPlaceholder(date, (d) => {
    const p = partsInTimeZone(d, zone);
    const label = zoneLabel?.trim() || (zone === "UTC" ? "Z" : zoneAbbr(d, zone));
    return `${p.hour}:${p.minute}:${p.second} ${label}`;
  });
}

/** Header card time in an IANA zone. The abbreviation sits beside the Local label. */
export function formatZoneCardTime(date: ClockInstant, timeZone: string): string {
  const zone = timeZone.trim() || "UTC";
  return formatOrPlaceholder(date, (d) => {
    const p = partsInTimeZone(d, zone);
    return `${p.hour}:${p.minute}:${p.second}`;
  });
}

/** Add calendar days on a zone wall, then snap to midnight (DST-safe). */
export function addDaysInTimeZone(date: Date, days: number, timeZone: string): Date {
  const p = partsInTimeZone(date, timeZone);
  const noon = wallTimeInTimeZone(timeZone, Number(p.year), Number(p.month), Number(p.day), 12, 0);
  noon.setTime(noon.getTime() + days * 86_400_000);
  const next = partsInTimeZone(noon, timeZone);
  return wallTimeInTimeZone(timeZone, Number(next.year), Number(next.month), Number(next.day), 0, 0);
}

/**
 * `datetime-local` whose wall clock is `timeZone` (UTC when zone is UTC).
 * Pair with fromZoneDatetimeLocal. Stored ISO stays Zulu.
 */
export function toZoneDatetimeLocal(date: ClockInstant, timeZone: string): string {
  const instant = coerceClockDate(date);
  if (!instant) return "";
  const p = partsInTimeZone(instant, timeZone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Parse a zone `datetime-local` wall clock back to UTC ISO. Empty / junk → "". */
export function fromZoneDatetimeLocal(raw: string, timeZone: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (timeZone === "UTC") return fromUtcDatetimeLocal(trimmed);
  const m = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return "";
  try {
    const date = wallTimeInTimeZone(
      timeZone,
      Number(m[1]),
      Number(m[2]),
      Number(m[3]),
      Number(m[4]),
      Number(m[5]),
      Number(m[6] ?? 0),
    );
    return isValidDate(date) ? date.toISOString() : "";
  } catch {
    return "";
  }
}

/**
 * `datetime-local` value whose wall clock is UTC (not the browser zone).
 * Pair with fromUtcDatetimeLocal when the dispatcher types a Zulu STD.
 */
export function toUtcDatetimeLocal(date: ClockInstant): string {
  const instant = coerceClockDate(date);
  if (!instant) return "";
  const p = utcParts(instant);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Parse a Zulu `datetime-local` string back to ISO. Empty / junk → "". */
export function fromUtcDatetimeLocal(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const m = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) {
    const fallback = new Date(trimmed.endsWith("Z") ? trimmed : `${trimmed}Z`);
    return Number.isFinite(fallback.getTime()) ? fallback.toISOString() : "";
  }
  const iso = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] ?? "00"}.000Z`;
  const parsed = new Date(iso);
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : "";
}

export function formatHalifaxDateTime(date: ClockInstant): string {
  return formatOrPlaceholder(date, (d) => {
    const p = halifaxParts(d);
    const mon = MONTHS[Number(p.month) - 1] ?? p.month;
    return `${p.day} ${mon} ${p.year}  ${p.hour}:${p.minute} ${halifaxAbbr(d)}`;
  });
}

/** Same shape as formatHalifaxDateTime, in the desk-local zone. */
export function formatDateTimeInZone(date: ClockInstant, timeZone: string): string {
  const zone = timeZone.trim() || "UTC";
  return formatOrPlaceholder(date, (d) => {
    const p = partsInTimeZone(d, zone);
    const mon = MONTHS[Number(p.month) - 1] ?? p.month;
    const label = zone === "UTC" ? "Z" : zoneAbbr(d, zone);
    return `${p.day} ${mon} ${p.year}  ${p.hour}:${p.minute} ${label}`;
  });
}

export function formatUtcDateTime(date: ClockInstant): string {
  return formatOrPlaceholder(date, (d) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    const mon = MONTHS[d.getUTCMonth()] ?? "";
    return `${pad(d.getUTCDate())} ${mon} ${d.getUTCFullYear()}  ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
  });
}

/** Persist both UTC ISO and a Halifax wall-clock label (desk is Nova Scotia). */
export function dualClockStamp(date?: ClockInstant): { utc: string; local: string } {
  const instant = coerceClockDate(arguments.length === 0 ? new Date() : date);
  if (!instant) return { utc: "", local: CLOCK_PLACEHOLDER };
  try {
    return {
      utc: instant.toISOString(),
      local: formatHalifaxDateTime(instant),
    };
  } catch {
    return { utc: "", local: CLOCK_PLACEHOLDER };
  }
}

export function roundUpToQuarter(date: Date): Date {
  const p = halifaxParts(date);
  let year = Number(p.year);
  let month = Number(p.month);
  let day = Number(p.day);
  let hour = Number(p.hour);
  let minute = Number(p.minute);
  const rem = minute % 15;
  if (rem !== 0 || Number(p.second) > 0) {
    minute += 15 - rem;
  }
  if (minute >= 60) {
    minute -= 60;
    hour += 1;
  }
  if (hour >= 24) {
    hour -= 24;
    const next = halifaxWallToDate(year, month, day, 12, 0);
    next.setTime(next.getTime() + 24 * 60 * 60 * 1000);
    const np = halifaxParts(next);
    year = Number(np.year);
    month = Number(np.month);
    day = Number(np.day);
  }
  return halifaxWallToDate(year, month, day, hour, minute, 0);
}

export function suggestDeparture(now: Date = new Date(), rng: () => number = Math.random): Date {
  const padMin = 45 + Math.floor(rng() * 9) * 15;
  let t = roundUpToQuarter(new Date(now.getTime() + padMin * 60_000));
  const hour = Number(halifaxParts(t).hour);
  if (hour >= 22 || hour < 6) {
    const p = halifaxParts(t);
    let year = Number(p.year);
    let month = Number(p.month);
    let day = Number(p.day);
    if (hour >= 22) {
      const noon = halifaxWallToDate(year, month, day, 12, 0);
      noon.setTime(noon.getTime() + 24 * 60 * 60 * 1000);
      const np = halifaxParts(noon);
      year = Number(np.year);
      month = Number(np.month);
      day = Number(np.day);
    }
    const morningHour = 7 + Math.floor(rng() * 3);
    const morningMin = [0, 15, 30, 45][Math.floor(rng() * 4)] ?? 0;
    t = halifaxWallToDate(year, month, day, morningHour, morningMin, 0);
  }
  return t;
}

export function missionClockStatus(
  departureIso: string,
  eteMin: number,
  now: Date = new Date(),
): "pending" | "in-progress" | "completed" {
  const dep = Date.parse(departureIso);
  if (!Number.isFinite(dep)) return "pending";
  const arr = dep + eteMin * 60_000;
  const t = now.getTime();
  if (t < dep) return "pending";
  if (t < arr) return "in-progress";
  return "completed";
}
